import * as React from 'react';
import { connect } from 'react-redux';
import { Container } from 'semantic-ui-react';

// Component
import UserDetails from 'src/components/Users/details';

// Models
import {
  Account,
  AccountType,
  Role
} from '@120wateraudit/envirio-components/dist/models';
import { User } from 'src/types/AccountManagementTypes';

// Actions
import { fetchRolesRequest } from 'src/actions/roles';
import UserActions from 'src/actions/users';

// Utils
import { omit } from 'lodash';
import { getFormValues } from 'redux-form';

// API
import { APIProvider } from 'src/utils/API';
import { ApplicationState } from 'src/reducers';

// Selectors
import { getRoles, getRolesAsSelectList } from 'src/selectors/roles';

interface Props {
  formUser: User;
  isFetching: boolean;
  match: any;
  roleOptions: any;
  roles: Role[];
  user: User;

  // Actions
  fetchRolesRequest: () => void;
  fetchUser: ({ userId }: { userId: number }) => void;
  unloadUser: () => void;
  updateUser: ({ user }: { user: User }) => void;
}

interface State {
  addingToAccount: boolean;
  defaultAccounts: { [key in string]: Account | null };
  defaultAccountsChanged: boolean;
  currentRoles: any[];
  editingRoles: boolean;
  isSaving: boolean;
  rolesAccount: undefined | Account;
}

class UserDetailsContainer extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      addingToAccount: false,
      defaultAccounts: {},
      defaultAccountsChanged: false,
      currentRoles: [],
      editingRoles: false,
      isSaving: false,
      rolesAccount: undefined
    };
  }

  componentWillMount() {
    this.getAllRoles();
    this.fetchUser();
    this.fetchDefaultAccounts();
  }

  componentWillUnmount() {
    this.props.unloadUser();
  }

  getAllRoles = () => {
    this.props.fetchRolesRequest();
  };

  fetchUser = () => {
    this.props.fetchUser({
      userId: this.props.match.params.id
    });
  };

  fetchDefaultAccounts = async () => {
    try {
      const defaultAccounts: {
        [key in string]: Account;
      } = await APIProvider.fetch(
        `/platform/account-management/rest/users/${this.props.match.params.id}/defaultaccounts`
      );

      this.setState({
        defaultAccounts,
        defaultAccountsChanged: false
      });
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.log(e);
    }
  };

  setDefaultAccount = (type: number, account: Account | null): void => {
    const defaultAccounts = this.state.defaultAccounts;
    defaultAccounts[AccountType[type]] = account;
    this.setState({ defaultAccounts, defaultAccountsChanged: true });
  };

  updateDefaultAccounts = async (
    defaultAccounts: { [key in string]: Account | null }
  ) => {
    try {
      const payload: { [key: string]: number | null | undefined } = {};
      Object.keys(defaultAccounts).forEach(k => {
        payload[k] = k in defaultAccounts ? defaultAccounts[k]?.id : null;
      });
      await APIProvider.put(
        `/platform/account-management/rest/users/${this.props.match.params.id}/defaultaccounts`,
        payload
      );
      await this.fetchDefaultAccounts();
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.log(e);
    }
  };

  onUpdateClicked = async () => {
    const user = this.props.formUser;
    const { defaultAccounts, defaultAccountsChanged } = this.state;

    if (defaultAccountsChanged) {
      await this.updateDefaultAccounts(defaultAccounts);
      this.setState({ defaultAccountsChanged: false });
    }

    this.props.updateUser({
      user: omit(user, ['roles', 'accounts', 'defaultAccountId']) as User
    });
  };

  toggleEditingRoles = () => {
    this.setState({
      currentRoles: [],
      editingRoles: !this.state.editingRoles,
      rolesAccount: undefined
    });
  };

  toggleAddingToAccount = () => {
    this.setState({
      addingToAccount: !this.state.addingToAccount
    });
  };

  render() {
    const { isFetching, roleOptions, user } = this.props;

    const {
      addingToAccount,
      defaultAccounts,
      editingRoles,
      isSaving,
      rolesAccount
    } = this.state;

    return (
      <Container>
        <UserDetails
          addingToAccount={addingToAccount}
          defaultAccounts={defaultAccounts}
          defaultAccountSetter={this.setDefaultAccount}
          editingRoles={editingRoles}
          isFetching={isFetching}
          isSaving={isSaving}
          onUpdateClicked={this.onUpdateClicked}
          roleOptions={roleOptions}
          rolesAccount={rolesAccount}
          roles={this.props.roles}
          toggleAddingToAccount={this.toggleAddingToAccount}
          toggleEditingRoles={this.toggleEditingRoles}
          user={user}
          fetchUser={this.fetchUser}
        />
      </Container>
    );
  }
}

const mapState = (state: ApplicationState) => ({
  formUser: getFormValues('user')(state),
  isFetching: state.user.isFetching,
  roleOptions: getRolesAsSelectList(state),
  roles: getRoles(state),
  user: state.user.item
});

const mapDispatchToProps = {
  fetchRolesRequest,
  fetchUser: UserActions.detailsActions.fetchRequest,
  unloadUser: UserActions.detailsActions.unload,
  updateUser: UserActions.updateActions.updateRequest
};

export default connect(mapState, mapDispatchToProps)(UserDetailsContainer);
